import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  MuiBox,
  Card,
  SvgIconButton,
  FlexBox,
  MuiTable,
  Collapse,
  SelectDropdown,
  SvgIcon,
  Tooltip
} from '@kis-technologies/kis-ui';
import { ConfigurationsProps } from '../../types';
import get from 'lodash.get';
import { useStyles } from '../../styled';
import { ConfigEditDrawer } from '../ConfigEditDrawer';
import { getFormattedValue, formatConfigTitle } from '../../utils';
import { ActionOverlay } from 'components/ActionOverlay';

export const Configurations = (props: ConfigurationsProps) => {
  const { configData, configType, columns, onDeleteConfirm, setDrawerVisibility } = props;
  const columnKeys = columns.reduce((acc: string[], cur) => {
    acc.push(cur.key);
    return acc;
  }, []);
  const dropDownColumns =
    configData &&
    Object.keys(configData)
      .filter((column) => columnKeys.includes(column))
      .sort();

  const [selectedColumn, setSelectedColumn] = useState<string>(dropDownColumns ? dropDownColumns[0] : '');
  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [selectedSubProperty, setSelectedSubProperty] = useState<string>('');
  const [selectedDataType, setSelectedDataType] = useState<string>('string');
  const [selectedPropertyValue, setSelectedPropertyValue] = useState<boolean | number | string | object>('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [showAddConfig, setShowAddConfig] = useState(false);
  const [addConfigDropdown, setAddConfigDropdown] = useState<typeof columns>([]);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const classes = useStyles({ showDrawer })();
  const filtered = get({ ...configData }, selectedColumn);

  useEffect(() => {
    const selectedDropDown = columns.find((item) => item.key === selectedColumn && item.type === 'object');
    let addMoreConfigCount = 0;
    if (selectedDropDown && selectedDropDown.children) {
      addMoreConfigCount = selectedDropDown.children
        .reduce((acc: string[], cur) => {
          acc.push(cur.key);
          return acc;
        }, [])
        .filter((column) => !Object.keys(filtered).includes(column)).length;
    }
    setShowAddConfig(addMoreConfigCount > 0);
    setExpandedRows({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumn]);

  const getFormattedItem = (column: string) => {
    const columnItem = columns.find((item) => item.key === column);
    if (columnItem && columnItem.type) {
      return formatConfigTitle(column, columnItem.type);
    }

    return column;
  };

  const getSelectedType = (selectedCol: string, property: string, subProperty?: string): string => {
    let selected = columns.find((column) => column.key === selectedCol);
    if (subProperty && selected && selected.children) {
      selected = selected.children.find((column) => column.key === subProperty);
    }
    if (property !== selectedCol && selected && selected.children) {
      selected = selected.children.find((column) => column.key === property);
    }
    return selected ? selected.type : 'object';
  };

  const hasChildrenDefined = (selectedCol: string, property: string): boolean => {
    let hasChildren = false;
    const selected = columns.find((cl) => cl.key === selectedCol);
    if (selected && selected.children && selected.children.length) {
      const selectedChildren = selected.children.find((chld) => chld.key === property);
      if (selectedChildren && selectedChildren.children && selectedChildren.children.length) {
        hasChildren = true;
      }
    }

    return hasChildren;
  };

  const setEditDrawerVisibility = (status: boolean) => {
    setShowDrawer(status);
    setDrawerVisibility(status);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onEditClick = (selectedCol: string, property: string, value: any, subProperty?: string) => {
    setSelectedDataType(getSelectedType(selectedCol, property, subProperty));
    setSelectedProperty(property);
    setSelectedSubProperty(subProperty || '');
    setAddConfigDropdown([]);
    setSelectedPropertyValue(value === null || value === undefined ? '' : value);
    setSelectedColumn(selectedCol);
    setEditDrawerVisibility(true);
  };

  const getValidDropdownOptions = (selectedCol: string, subProperty?: string): typeof columns => {
    let dropDownOptions: typeof columns = [];
    const selected = columns.find((item) => item.key === selectedCol && item.type === 'object');
    if (selected && selected.children) {
      if (subProperty) {
        const selectedChildren = selected.children.find((chld) => chld.key === subProperty && chld.type === 'object');
        if (selectedChildren && selectedChildren.children) {
          dropDownOptions = selectedChildren.children.filter(
            (item) => filtered[subProperty] && !Object.keys(filtered[subProperty]).includes(item.key)
          );
        }
      } else {
        dropDownOptions = selected.children.filter((item) => !Object.keys(filtered).includes(item.key));
      }
    }

    return dropDownOptions;
  };

  const onAddConfigClick = (selectedCol: string, subProperty?: string) => {
    const dropDownOptions = getValidDropdownOptions(selectedCol, subProperty);
    setAddConfigDropdown(dropDownOptions);
    setSelectedPropertyValue('');
    setSelectedSubProperty(subProperty || '');
    setSelectedColumn(selectedColumn);
    setEditDrawerVisibility(true);
  };

  const onDeleteConfigClick = (key: string, subProperty?: string) => {
    setSelectedProperty(key);
    setSelectedSubProperty(subProperty || '');
    setShowDeleteOverlay(true);
  };

  const onConfigDeleteConfirm = () => {
    let payload = {};
    const name = selectedProperty;
    if (selectedSubProperty) {
      const subName = selectedSubProperty;
      const { [subName]: removedProperty, ...remainingProperty } = filtered[selectedProperty];
      payload = {
        id: configData && configData.id,
        [selectedColumn]: { ...filtered, [name]: remainingProperty }
      };
    } else {
      const { [name]: removedProperty, ...remainingPayload } = filtered;
      payload = {
        id: configData && configData.id,
        [selectedColumn]: remainingPayload
      };
    }
    onDeleteConfirm(configType, payload);
    setShowDeleteOverlay(false);
  };

  const resetOverlay = () => {
    setEditDrawerVisibility(false);
    setShowDeleteOverlay(false);
    setSelectedProperty('');
    setSelectedPropertyValue('');
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getSubRow = (value: any, key: string) => {
    return Object.keys(value)
      .sort()
      .map((property) => (
        <MuiTable.TableRow className={classes.configTableSubRows}>
          <MuiTable.TableCell className={classes.configSubPropertyCell}>
            <Typography fontSize={16}>{property}</Typography>
          </MuiTable.TableCell>
          <MuiTable.TableCell className={classes.configTableSubValueCell}>
            <Typography fontSize={16}>{getFormattedValue(value[property])}</Typography>
          </MuiTable.TableCell>
          <MuiTable.TableCell className={classes.configSubActionCell}>
            <FlexBox align="stretch" fullWidth justify="end">
              <SvgIconButton
                onClick={() => onDeleteConfigClick(key, property)}
                data-testid="delete-config-button"
                style={{ marginRight: '10px' }}
              >
                <SvgIcon name="exit_circle" />
              </SvgIconButton>
              <SvgIconButton
                onClick={() => onEditClick(selectedColumn, property, value[property], key)}
                data-testid="update-user-button"
              >
                <SvgIcon name="edit_circle" />
              </SvgIconButton>
            </FlexBox>
          </MuiTable.TableCell>
        </MuiTable.TableRow>
      ));
  };

  const ConfigTableRow = (rowProps: {
    idx?: number;
    property: string;
    value: string | number;
    editAction: () => void;
    deleteAction?: () => void;
  }) => {
    const { idx = 0, property, value } = rowProps;
    const hasChildren = hasChildrenDefined(selectedColumn, property);
    const isExpand = expandedRows[idx];
    const dropDownOptions = getValidDropdownOptions(selectedColumn, property);
    const handleClick = () => {
      if (hasChildren) {
        setExpandedRows({ ...expandedRows, [idx]: isExpand ? false : true });
      }
    };

    return (
      <>
        <MuiTable.TableRow
          className={classes.configTableRows}
          onClick={handleClick}
          sx={hasChildren ? { cursor: 'pointer' } : {}}
        >
          <MuiTable.TableCell className={classes.configPropertyCell}>
            <Typography fontSize={16}>{property}</Typography>
          </MuiTable.TableCell>
          <MuiTable.TableCell className={classes.configTableValueCell}>
            {!isExpand && <Typography fontSize={16}>{getFormattedValue(value)}</Typography>}
          </MuiTable.TableCell>
          <MuiTable.TableCell className={classes.configActionCell}>
            <FlexBox align="stretch" fullWidth justify="end">
              {isExpand && dropDownOptions.length > 0 && (
                <SvgIconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddConfigClick(selectedColumn, property);
                  }}
                  data-testid="delete-config-button"
                  style={{ marginRight: '10px' }}
                >
                  <SvgIcon name="plus_circle" />
                </SvgIconButton>
              )}
              {rowProps.deleteAction && (
                <SvgIconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    if (rowProps.deleteAction) rowProps.deleteAction();
                  }}
                  data-testid="delete-config-button"
                  style={{ marginRight: '10px' }}
                >
                  <SvgIcon name="exit_circle" />
                </SvgIconButton>
              )}
              <SvgIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  rowProps.editAction();
                }}
                data-testid="update-user-button"
              >
                <SvgIcon name="edit_circle" />
              </SvgIconButton>
            </FlexBox>
          </MuiTable.TableCell>
        </MuiTable.TableRow>
        <MuiTable.TableRow
          className={classes.tableSubRows}
          sx={isExpand ? { marginBottom: '10px' } : { display: 'none' }}
        >
          <MuiTable.TableCell sx={{ padding: '0px', flex: '1', borderRadius: '8px' }} colSpan={3}>
            <Collapse in={isExpand} timeout="auto" unmountOnExit>
              {isExpand ? (
                <MuiTable.Table size="small" aria-label="cart-sub-table" className={classes.subTable}>
                  <MuiTable.TableBody>{getSubRow(value as string, property)}</MuiTable.TableBody>
                </MuiTable.Table>
              ) : null}
            </Collapse>
          </MuiTable.TableCell>
        </MuiTable.TableRow>
      </>
    );
  };

  const isJsonCategory = ['object', 'array'].includes(getSelectedType(selectedColumn, selectedColumn));

  return (
    <>
      <FlexBox direction="column" sx={{ flexFlow: 'column wrap', flex: '1 1 auto' }} maxWidth="100%">
        <FlexBox direction="row" margin="0 6 6" align="center">
          <Typography fontSize={24} fontWeight={700}>
            Category
          </Typography>
          <Box width={20} />
          <SelectDropdown
            items={dropDownColumns ? dropDownColumns : ['']}
            itemToString={getFormattedItem}
            selectedItem={selectedColumn}
            onChange={(column: string) => setSelectedColumn(column)}
            placeholder="Select a category"
            data-testid="media-selector"
          />
        </FlexBox>
        {/* TABLE */}
        <MuiBox className={classes.configTableOuterBox}>
          <Card hasBorderRadius raised fullHeight sx={{ width: '100%', minWidth: 0, flex: '1 1 auto' }}>
            <MuiTable.TableContainer id="userViewTable" className={classes.configTableContainer}>
              <MuiTable.Table stickyHeader sx={{ borderCollapse: 'collapse', width: '100%' }}>
                <MuiTable.TableHead>
                  <MuiTable.TableRow className={classes.configTableRows}>
                    <MuiTable.TableCell className={classes.configHeadersProperty}>Property</MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.configTableHeaders}>Value</MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.configActionHeader}>Action</MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>
                <MuiTable.TableBody>
                  {filtered && getSelectedType(selectedColumn, selectedColumn) === 'object'
                    ? Object.keys(filtered)
                        .sort()
                        .map((property, idx) => (
                          <ConfigTableRow
                            key={idx}
                            idx={idx}
                            property={property}
                            value={filtered[property]}
                            editAction={() => onEditClick(selectedColumn, property, filtered[property])}
                            deleteAction={() => onDeleteConfigClick(property)}
                          />
                        ))
                    : null}
                  {['configuration', 'clientIntegration', 'area', 'integration'].includes(
                    getSelectedType(selectedColumn, selectedColumn)
                  ) && (
                    <ConfigTableRow
                      property={formatConfigTitle(selectedColumn, getSelectedType(selectedColumn, selectedColumn))}
                      value={filtered.name}
                      editAction={() => onEditClick(selectedColumn, selectedColumn, filtered)}
                    />
                  )}
                  {!['object', 'configuration', 'clientIntegration', 'area', 'integration'].includes(
                    getSelectedType(selectedColumn, selectedColumn)
                  ) && (
                    <ConfigTableRow
                      property={selectedColumn}
                      value={filtered}
                      editAction={() => onEditClick(selectedColumn, selectedColumn, filtered)}
                    />
                  )}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Card>
          {isJsonCategory && (
            <FlexBox
              align="center"
              noWrap
              height="70px"
              sx={{ padding: '16px 0 16px 16px', flexShrink: 0, gap: '10px' }}
            >
              {showAddConfig && getValidDropdownOptions(selectedColumn).length > 0 && (
                <Tooltip title="Add Config" arrow placement={'top'} verticalOffset={-8}>
                  <SvgIconButton onClick={() => onAddConfigClick(selectedColumn)}>
                    <SvgIcon name="plus_circle" />
                  </SvgIconButton>
                </Tooltip>
              )}
              <Tooltip title="Edit Category" arrow placement={'top'} verticalOffset={-8}>
                <SvgIconButton
                  onClick={() => onEditClick(selectedColumn, selectedColumn, filtered)}
                  data-testid="edit-category-button"
                >
                  <SvgIcon name="edit_circle" />
                </SvgIconButton>
              </Tooltip>
            </FlexBox>
          )}
        </MuiBox>
      </FlexBox>

      {showDrawer && (
        <ConfigEditDrawer
          onClose={resetOverlay}
          isOpen={showDrawer}
          configData={configData}
          selectedColumn={selectedColumn}
          selectedColumnValue={filtered}
          selectedProperty={selectedProperty}
          selectedPropertyValue={selectedPropertyValue}
          selectedSubProperty={selectedSubProperty}
          selectedDataType={selectedDataType}
          configType={configType}
          addConfigDropdown={addConfigDropdown}
        />
      )}

      {showDeleteOverlay && (
        <ActionOverlay
          title={
            <MuiBox className={classes.configDeleteHeading}>
              <Typography className={classes.configDeleteHeader} sx={{ lineHeight: 1, fontWeight: 600 }}>
                Are you sure you want to delete {selectedSubProperty ? selectedSubProperty : selectedProperty}?
              </Typography>
            </MuiBox>
          }
          confirmText="Delete"
          onConfirm={() => onConfigDeleteConfirm()}
          cancelText="Back"
          onCancel={resetOverlay}
          width={800}
          appearance="error"
        />
      )}
    </>
  );
};
